import { singleton, inject } from "tsyringe";

// shared
import type { HttpAdapter } from "@/shared/libs/http/interface";

// domain
import {
    ChallengeTemplateRepository,
    RequestGetChallengeTemplates,
    PostBeforeChallengeTemplatePayload,
    PostAfterChallengeTemplatePayload,
} from "@/entities/challenge-template/domain/repository";
import { ChallengeOrder, ChallengeTemplateType } from "@/entities/challenge-template/domain/enums";
import type {
    GetChallengeTemplatesResponseDto,
    ChallengeTemplateDto,
} from "@/entities/challenge-template/domain/dtos";

@singleton()
export default class ChallengeTemplateRepositoryImpl implements ChallengeTemplateRepository {
    constructor(@inject("HttpAdapter") private readonly server: HttpAdapter) {}

    /** @description 챌린지 템플릿 목록 조회 (GET /v2/challenge-templates) */
    getChallengeTemplates = async (request: RequestGetChallengeTemplates) => {
        let url = `/v2/challenge-templates?`;

        url += `academyId=${request.academyId}&`;

        // 챌린지 타입 반복 추가
        request.type.forEach((type) => {
            url += `type=${type}&`;
        });

        if (request.from) {
            url += `from=${request.from}&`;
        }

        if (request.to) {
            url += `to=${request.to}&`;
        }

        if (request.keyword) {
            url += `keyword=${request.keyword}&`;
        }

        url += `size=${request.size}&`;

        // v2: orders(JSON 배열). OrderDirection: 오름차순 0, 내림차순 1
        // 수심달 고사(Exam)만 조회: title 오름차순 고정. 그 외: _id + 목록 정렬 옵션
        const direction = request.order === ChallengeOrder.Latest ? 1 : 0;
        const isExamOnly =
            request.type.length === 1 && request.type[0] === ChallengeTemplateType.Exam;
        const ordersPayload = isExamOnly ? [{ title: 0 }] : [{ _id: direction }];
        url += `orders=${encodeURIComponent(JSON.stringify(ordersPayload))}&`;

        if (request.nextToken) {
            url += `nextToken=${encodeURIComponent(request.nextToken)}`;
        }

        const response = await this.server.get<GetChallengeTemplatesResponseDto>(url);

        return response;
    };

    /** @description 챌린지 템플릿 삭제 */
    deleteChallengeTemplate = async (challengeTemplateId: string) => {
        await this.server.delete(`/v1/challenge-templates/${challengeTemplateId}`);
    };

    /**
     * @description 비포챌린지 템플릿 생성
     * @param {PostBeforeChallengeTemplatePayload} payload - 비포챌린지 템플릿 생성 요청 데이터
     */
    postBeforeChallengeTemplate = async (payload: PostBeforeChallengeTemplatePayload) => {
        const response = await this.server.post<ChallengeTemplateDto>(
            "/v1/challenge-templates",
            payload,
        );

        return response;
    };

    /**
     * @description 애프터 챌린지 템플릿 생성
     * @param {PostAfterChallengeTemplatePayload} payload - 애프터 챌린지 템플릿 생성 요청 데이터
     */
    postAfterChallengeTemplate = async (payload: PostAfterChallengeTemplatePayload) => {
        const response = await this.server.post<ChallengeTemplateDto>(
            "/v1/challenge-templates",
            payload,
        );

        return response;
    };
}
